import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { AuthApi } from "@/api/auth.api";
import { useNavigate } from "react-router-dom";
import {
  KeyRound,
  Mail,
  LockKeyhole,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";

const RESEND_TIMER = 30; // seconds

const PASSWORD_RULE_MESSAGE =
  "Password must be at least 7 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.";

const isPasswordValid = (value: string) =>
  value.length >= 7 &&
  /[A-Z]/.test(value) &&
  /[a-z]/.test(value) &&
  /\d/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"email" | "code" | "reset" | "done">("email");
  const [code, setCode] = useState(["", "", "", ""]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_TIMER);
  const [resetPasswordToken, setResetPasswordToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (step === "code" && resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(t => t - 1), 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [step, resendTimer]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await AuthApi.forgotPassword(email);
      toast({
        title: "Security code sent",
        description: "If the account exists, a password reset code has been sent to your email if it exists in our system.",
      });
      setStep("code");
      setResendTimer(RESEND_TIMER);
    } catch (err: any) {
      toast({
        title: "Failed to send code",
        description: err?.response?.data?.errorMessage || err?.message || "An error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.join('').length !== 4) {
      toast({ title: "Invalid code", description: "Please enter the 4-digit code.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await AuthApi.initiateResetPassword(email, code.join(''));
      toast({ title: "Code verified", description: "You can now reset your password." });
      setResetPasswordToken(res.data.resetPasswordToken);
      setStep("reset");
    } catch (err: any) {
      toast({
        title: "Invalid code",
        description: err?.response?.data?.errorMessage || err?.message || "An error occurred.",
        variant: "destructive",
      });
      setCode(["", "", "", ""]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await AuthApi.forgotPassword(email);
      toast({ title: "Security code resent", description: "Check your email for the new code." });
      setResendTimer(RESEND_TIMER);
    } catch (err: any) {
      toast({
        title: "Failed to resend code",
        description: err?.response?.data?.errorMessage || err?.message || "An error occurred.",
        variant: "destructive",
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid(password)) {
      toast({
        title: "Invalid password",
        description: PASSWORD_RULE_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords match.",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res: any = await AuthApi.resetPassword(
        email,
        password,
        resetPasswordToken
      );
      const data = res?.data;

      if (data?.isSuccess === false) {
        const message =
          data?.statusMessage ||
          data?.errorMessage ||
          "An error occurred.";
        toast({
          title: "Failed to reset password",
          description: String(message).replace(/^Error:\s*/i, ""),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Password reset successful",
        description: "You can now log in with your new password.",
      });
      setStep("done");
    } catch (err: any) {
      const message =
        err?.response?.data?.statusMessage ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        "An error occurred.";
      toast({
        title: "Failed to reset password",
        description: String(message).replace(/^Error:\s*/i, ""),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "email") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
        <div className="w-full max-w-md">
          <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 shadow-sm">
              <KeyRound className="text-blue-600 w-8 h-8" />
            </div>
            <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">Forgot Password</h2>
            <p className="text-center text-gray-500 mb-8 text-base">Enter your email to receive a security code.</p>
            <form onSubmit={handleEmailSubmit} className="space-y-6 w-full">
              <div className="space-y-1">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                    <Mail className="w-5 h-5" />
                  </span>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="m@example.com"
                    autoComplete="email"
                    required
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 pl-10 py-2 text-base"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-lg transition-all duration-200" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Security Code"}
              </Button>
            </form>
            <Button
              type="button"
              variant="outline"
              className="w-full mt-4"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "code") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
        <div className="w-full max-w-md">
          <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 shadow-sm">
              <Mail className="text-blue-600 w-8 h-8" />
            </div>
            <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">Enter Security Code</h2>
            <p className="text-center text-gray-500 mb-8 text-base">
              If the account exists, a password reset code has been sent to <span className="font-semibold text-blue-700">{email}</span>. Please enter the code below to continue.
            </p>
            <form onSubmit={handleCodeSubmit} className="space-y-6 w-full">
              <div className="flex justify-center gap-3 mb-4">
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="w-14 h-14 text-center text-2xl border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm bg-blue-50 text-blue-700 font-bold tracking-widest"
                    value={digit}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      if (!val) return;
                      const newCode = [...code];
                      newCode[idx] = val;
                      setCode(newCode);
                      // Move to next box
                      if (val && idx < 3) {
                        const next = document.getElementById(`code-input-${idx + 1}`);
                        if (next) (next as HTMLInputElement).focus();
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Backspace" || e.key === "Delete") {
                        e.preventDefault();
                        const newCode = [...code];
                        if (code[idx]) {
                          newCode[idx] = "";
                          setCode(newCode);
                        } else if (idx > 0) {
                          const prev = document.getElementById(`code-input-${idx - 1}`);
                          if (prev) (prev as HTMLInputElement).focus();
                          newCode[idx - 1] = "";
                          setCode(newCode);
                        }
                      }
                    }}
                    id={`code-input-${idx}`}
                    autoFocus={idx === 0}
                  />
                ))}
              </div>
              <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-lg transition-all duration-200" disabled={isSubmitting}>
                {isSubmitting ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
            <div className="mt-6 flex flex-col items-center w-full">
              <Button
                type="button"
                variant="ghost"
                className="text-blue-600 hover:underline text-sm"
                onClick={handleResend}
                disabled={resendLoading || resendTimer > 0}
              >
                {resendLoading ? "Resending..." : resendTimer > 0 ? `Resend code in ${resendTimer}s` : "Resend code"}
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full mt-4"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "reset") {
    const passwordValid = isPasswordValid(password);
    const confirmPasswordMatches =
      confirmPassword.length > 0 && password === confirmPassword;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
        <div className="w-full max-w-md">
          <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 shadow-sm">
              <LockKeyhole className="text-blue-600 w-8 h-8" />
            </div>
            <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">Reset Password</h2>
            <p className="text-center text-gray-500 mb-8 text-base">Enter your new password below.</p>
            <form onSubmit={handleResetPassword} className="space-y-6 w-full">
              <div className="space-y-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                    <LockKeyhole className="w-5 h-5" />
                  </span>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    required
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 pl-10 pr-10 py-2 text-base"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {password.length > 0 && !passwordValid && (
                  <p className="text-xs mt-1 text-red-500">
                    {PASSWORD_RULE_MESSAGE}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                    <LockKeyhole className="w-5 h-5" />
                  </span>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    required
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 pl-10 pr-10 py-2 text-base"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && !confirmPasswordMatches && (
                  <p className="text-xs mt-1 text-red-500">
                    Passwords do not match.
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500">{PASSWORD_RULE_MESSAGE}</p>
              <Button
                type="submit"
                className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-lg transition-all duration-200"
                disabled={
                  isSubmitting || !passwordValid || !confirmPasswordMatches
                }
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
            <Button
              type="button"
              variant="outline"
              className="w-full mt-4"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
        <div className="w-full max-w-md">
          <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4 shadow-sm">
              <CheckCircle2 className="text-green-600 w-8 h-8" />
            </div>
            <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">Password Reset Successful</h2>
            <p className="text-center text-gray-500 mb-8 text-base">Your password has been reset. You can now log in with your new password.</p>
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-lg transition-all duration-200" onClick={() => navigate("/login")}>Go to Login</Button>
          </div>
        </div>
      </div>
    );
  }
};

export default ForgotPassword; 